import { type ComponentPropsWithoutRef, forwardRef } from 'react'
import { ArrowUp } from '../../icons/arrow-up'
import { Square } from '../../icons/square'
import { cn } from '../../lib/cn'
import { Button } from '../button/button'

/**
 * The chat composer's chrome, shared by everything on this product that takes
 * a prompt: the Agent's input and the Analytics page editor's writing
 * assistant. It is the box and nothing else — the field and the controls are
 * the caller's, because the Agent's is a rich editor with mentions and the
 * editor's is a textarea.
 *
 * It exists because the two were drawn separately and read as two products:
 * one was a dialog with a bordered textarea in it, the other the rounded card
 * people recognise as "where you talk to it".
 */
export const Composer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function Composer({ className, ...props }, ref) {
    return <div ref={ref} className={cn('composer', className)} {...props} />
  }
)

/** The controls beneath the field. Anything with `composer-row__end` is pushed right. */
export function ComposerRow({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('composer-row', className)} {...props} />
}

export interface ComposerSendProps {
  /** Accessible name for the send button, and for the stop button when sending. */
  label: string
  stopLabel?: string
  /** Sending: the button becomes a stop control when `onStop` is supplied. */
  sending?: boolean
  disabled?: boolean
  onSubmit: () => void
  onStop?: () => void
  className?: string
}

export function ComposerSend({
  label,
  stopLabel,
  sending = false,
  disabled = false,
  onSubmit,
  onStop,
  className,
}: ComposerSendProps) {
  const stop = sending && onStop
  return (
    <Button
      type='button'
      variant='ghost'
      aria-label={stop ? (stopLabel ?? label) : label}
      title={stop ? (stopLabel ?? label) : label}
      disabled={!stop && disabled}
      onClick={stop ? onStop : onSubmit}
      className={cn('composer-send', className)}
    >
      {stop ? (
        <Square className='composer-send__icon' />
      ) : (
        <ArrowUp className='composer-send__icon' />
      )}
    </Button>
  )
}
